import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "edit-ticket-content__scrollable" }
const _hoisted_2 = { class: "edit-ticket-content__article" }
const _hoisted_3 = { class: "edit-ticket-content__article__table-container" }
const _hoisted_4 = { class: "edit-ticket-content__article" }
const _hoisted_5 = { class: "edit-ticket-content__article__table-container" }

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import {
  archiveAttachments,
  Attachment,
  deleteAttachments,
  getAttachmentsPage
} from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import { orderAlphabetical } from '@/utils/sort'

import AccordionCustomTickets from '@/components/features/tickets/AccordionCustomTickets.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableFilesMini from '@/components/features/attachments/TableFilesMini.vue'
import TableLinksMini from '@/components/features/attachments/TableLinksMini.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'EditTicketContentAttachments',
  setup(__props) {

const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const accordionAttachments1Active = ref(true)
const accordionAttachments2Active = ref(true)
const isLoadingFiles = ref(false)
const isLoadingLinks = ref(false)
const showConfirmDeleteFile = ref(false)
const showConfirmDeleteLink = ref(false)

const { filesList, filesUploaded, linksList, linksUpdated, selectedTicket } =
  storeToRefs(piniaStore)

const filesFields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: filesList.value?.length
        ? filesList.value.length + ' ' + t('general__label__files').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})
const linksFields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: linksList.value?.length
        ? linksList.value.length + ' ' + t('general__data__links').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})

async function archiveFile() {
  if (!piniaStore.selectedFile) return
  try {
    const id = piniaStore.selectedFile.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
    }
    if (!response?.data.success) {
      showArchiveFileError()
    }
  } catch (e) {
    showArchiveFileError()
    console.error(e)
  }
}
async function archiveLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
    }
    if (!response?.data.success) {
      showArchiveLinkError()
    }
  } catch (e) {
    showArchiveLinkError()
    console.error(e)
  }
}
async function deleteFile() {
  if (!piniaStore.selectedFile) return
  try {
    const entityId = piniaStore.selectedFile.entityId
    const id = piniaStore.selectedFile.attachmentId
    if (!entityId || !id) return
    const response = await deleteAttachments([piniaStore.selectedFile.attachmentId])
    if (response?.data.success) {
      // remove file from filesList in store
      const filesArray = piniaStore.filesList.filter((file) => file.attachmentId !== id)
      piniaStore.setFilesList(filesArray)
      piniaStore.setSelectedFile(null)
    }
    if (!response?.data.success) {
      showDeleteFileError()
    }
  } catch (e) {
    showDeleteFileError()
    console.error(e)
  }
}
async function deleteLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await deleteAttachments([piniaStore.selectedLink.attachmentId])
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
    }
    if (!response?.data.success) {
      showDeleteLinkError()
    }
  } catch (e) {
    showDeleteLinkError()
    console.error(e)
  }
}
async function downloadFile() {
  if (!piniaStore.selectedFile) return
  try {
    const res = await getAttachmentFileBlob(piniaStore.selectedFile)
    if (!res) return setDownloadFailedMessage()
    // @ts-ignore
    const fileUrl = window.URL.createObjectURL(new Blob([res.data]))
    const fileLink = document.createElement('a')
    fileLink.href = fileUrl
    fileLink.setAttribute('download', piniaStore.selectedFile.name)
    document.body.appendChild(fileLink)
    fileLink.click()
  } catch (e) {
    setDownloadFailedMessage()
    console.error(e)
  }
}
async function getFilesList(ticketId: string) {
  isLoadingFiles.value = true
  const filesArray: Attachment[] = []
  const filesPage = await getAttachmentsPage(
    ticketId,
    ['FILE'],
    0,
    100, // Might need adjustment in the future, but currently we don't expect large amounts of files on tickets
    'nameLower,asc',
    null,
    false
  )
  filesPage?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
  isLoadingFiles.value = false
}
async function getLinksList(ticketId: string) {
  isLoadingLinks.value = true
  const linksArray: Attachment[] = []
  const linksPage = await getAttachmentsPage(ticketId, ['LINK'], 0, 100, 'name,asc', null, false)
  linksPage?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
  isLoadingLinks.value = false
}
function setDownloadFailedMessage() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-download-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showArchiveFileError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-archive-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showArchiveLinkError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__link-archive-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showDeleteFileError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__file-delete-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showDeleteLinkError() {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${t('general__data__link-delete-failed')}, ${t(
      'general__text__retry-later-again'
    ).toLowerCase()}`
  })
}
function showModalCreateLink() {
  piniaStore.setCurrentOverlayModalOnModalPinia('editTicketLink')
  piniaStore.setShowOverlayModalOnModalPinia(true)
}
function showModalUploadFiles() {
  piniaStore.setCurrentOverlayModalOnModalPinia('uploadTicketFiles')
  piniaStore.setShowOverlayModalOnModalPinia(true)
}

watch(
  filesUploaded,
  (newFilesArray, oldFilesArray) => {
    if (newFilesArray.length && newFilesArray.length > oldFilesArray.length) {
      piniaStore.setFilesList([])
      if (selectedTicket.value) {
        getFilesList(selectedTicket.value.id)
      }
    }
  },
  { deep: true }
)
watch(
  linksUpdated,
  (newLinksArray, oldLinksArray) => {
    if (newLinksArray.length && newLinksArray.length > oldLinksArray.length) {
      piniaStore.setLinksList([])
      if (selectedTicket.value) {
        getLinksList(selectedTicket.value.id)
      }
    }
  },
  { deep: true }
)
onBeforeUnmount(() => {
  piniaStore.setSelectedFile(null)
  piniaStore.setSelectedLink(null)
  piniaStore.setFilesList([])
  piniaStore.setLinksList([])
})
onMounted(() => {
  if (selectedTicket.value) {
    if (store.getters.hasFileCreatorAccess) getFilesList(selectedTicket.value.id)
    if (store.getters.hasLinkCreatorAccess) getLinksList(selectedTicket.value.id)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (_unref(store).getters.hasFileReaderAccess)
      ? (_openBlock(), _createBlock(AccordionCustomTickets, {
          key: 0,
          accordionText: _unref(t)('general__data__files'),
          active: accordionAttachments1Active.value,
          class: "edit-ticket-content__accordion",
          onOnClicked: _cache[0] || (_cache[0] = ($event: any) => (accordionAttachments1Active.value = !accordionAttachments1Active.value))
        }, null, 8, ["accordionText", "active"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("article", _hoisted_2, [
      (showConfirmDeleteFile.value)
        ? (_openBlock(), _createBlock(OverlayCard, {
            key: 0,
            class: "overlay-confirm-delete",
            onCloseOverlay: _cache[3] || (_cache[3] = ($event: any) => (showConfirmDeleteFile.value = false))
          }, {
            default: _withCtx(() => [
              _createVNode(ConfirmDeleteAttachmentCard, {
                buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__file(s)').toLowerCase(),
                onCloseOverlay: _cache[1] || (_cache[1] = ($event: any) => (showConfirmDeleteFile.value = false)),
                onConfirmDelete: _cache[2] || (_cache[2] = ($event: any) => (deleteFile()))
              }, null, 8, ["buttonText"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(PulseLoader, {
          loading: isLoadingFiles.value,
          class: "spinner",
          color: "#1d234f"
        }, null, 8, ["loading"]),
        (!isLoadingFiles.value)
          ? (_openBlock(), _createBlock(TableFilesMini, {
              key: 0,
              class: "edit-ticket-content__article__table-container__table",
              fields: filesFields.value,
              items: _unref(filesList)?.length ? _unref(filesList) : [],
              onArchiveFile: archiveFile,
              onDeleteFile: _cache[4] || (_cache[4] = ($event: any) => (showConfirmDeleteFile.value = true)),
              onDownloadFile: downloadFile
            }, null, 8, ["fields", "items"]))
          : _createCommentVNode("", true)
      ]),
      (
          _unref(store).getters.hasErrandEditorAccess &&
          _unref(store).getters.hasFileCreatorAccess &&
          _unref(selectedTicket)?.active
        )
        ? (_openBlock(), _createBlock(ButtonText, {
            key: 1,
            class: "edit-ticket-content__article__button",
            buttonText: _unref(t)('general__data__file-upload-a'),
            iconLeft: require('@/design-system/assets/icons/Icon-Upload-Small.svg'),
            size: 'small',
            onOnClicked: showModalUploadFiles
          }, null, 8, ["buttonText", "iconLeft"]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, accordionAttachments1Active.value && _unref(store).getters.hasFileReaderAccess]
    ]),
    (_unref(store).getters.hasFileReaderAccess && _unref(store).getters.hasLinkReaderAccess)
      ? (_openBlock(), _createBlock(DividerHorizontal, { key: 1 }))
      : _createCommentVNode("", true),
    (_unref(store).getters.hasLinkReaderAccess)
      ? (_openBlock(), _createBlock(AccordionCustomTickets, {
          key: 2,
          accordionText: _unref(t)('general__data__links'),
          active: accordionAttachments2Active.value,
          class: "edit-ticket-content__accordion",
          onOnClicked: _cache[5] || (_cache[5] = ($event: any) => (accordionAttachments2Active.value = !accordionAttachments2Active.value))
        }, null, 8, ["accordionText", "active"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("article", _hoisted_4, [
      (showConfirmDeleteLink.value)
        ? (_openBlock(), _createBlock(OverlayCard, {
            key: 0,
            class: "overlay-confirm-delete",
            onCloseOverlay: _cache[8] || (_cache[8] = ($event: any) => (showConfirmDeleteLink.value = false))
          }, {
            default: _withCtx(() => [
              _createVNode(ConfirmDeleteAttachmentCard, {
                buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__link(s)').toLowerCase(),
                onCloseOverlay: _cache[6] || (_cache[6] = ($event: any) => (showConfirmDeleteLink.value = false)),
                onConfirmDelete: _cache[7] || (_cache[7] = ($event: any) => (deleteLink()))
              }, null, 8, ["buttonText"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(PulseLoader, {
          loading: isLoadingLinks.value,
          class: "spinner",
          color: "#1d234f"
        }, null, 8, ["loading"]),
        (!isLoadingLinks.value)
          ? (_openBlock(), _createBlock(TableLinksMini, {
              key: 0,
              class: "edit-ticket-content__article__table-container__table",
              fields: linksFields.value,
              items: _unref(linksList)?.length ? _unref(linksList) : [],
              onArchiveLink: archiveLink,
              onDeleteLink: _cache[9] || (_cache[9] = ($event: any) => (showConfirmDeleteLink.value = true))
            }, null, 8, ["fields", "items"]))
          : _createCommentVNode("", true)
      ]),
      (
          _unref(store).getters.hasErrandEditorAccess &&
          _unref(store).getters.hasLinkCreatorAccess &&
          _unref(selectedTicket)?.active
        )
        ? (_openBlock(), _createBlock(ButtonText, {
            key: 1,
            class: "edit-ticket-content__article__button",
            buttonText: _unref(t)('general__data__link-add-new'),
            iconLeft: require('@/design-system/assets/icons/Icon-Plus-Small.svg'),
            size: 'small',
            onOnClicked: showModalCreateLink
          }, null, 8, ["buttonText", "iconLeft"]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, accordionAttachments2Active.value && _unref(store).getters.hasLinkReaderAccess]
    ])
  ]))
}
}

})